import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { BuildingWing, CouponCampaign, Coupon, CouponCampaignKind, CouponCardSize, CouponPrintOverlayLayout, CurrentUser } from '../types';
import { BUILDING_WING_LABEL } from '../lib/buildingUnitUtils';
import { couponCampaignsService, couponsService, parseBuildingUnitList } from '../lib/database';
import { couponsToCSV, couponsToJSON, exportCouponsA4SheetPdf, exportCouponsPdfWithBarcodes, getCouponBarcodeValue } from '../lib/couponExport';
import { localEndOfDayFromDateInput } from '../lib/couponExpiry';
import {
  COUPON_CARD_SIZE_LABEL,
  cssAspectRatioForCardSize,
  getMmSheetLayoutConfig,
  isMmSheetCardSize,
  normalizeCouponCardSize,
  defaultCardSizeForCampaignKind,
  cardSizesForCampaignKind,
} from '../lib/couponCardSizes';
import {
  clampA4Rows,
  couponsPerSheet,
  gridHeightMm,
  maxA4Rows,
  printableHeightMmA4,
} from '../lib/couponSheetMm';
import {
  defaultPrintOverlayForKind,
  isPrintOverlayVisible,
  mergeCouponPrintOverlay,
  snapOverlayDragPct,
} from '../lib/couponPrintOverlay';
import { resolveCampaignTemplateDataUrl, resolveCampaignTemplateUrl } from '../lib/couponTemplateResolve';
import { tryCreatePdfCacheDataUrl, uploadCouponCampaignTemplateImage } from '../lib/couponTemplateImageUpload';

interface CouponCampaignsPageProps {
  currentUser: CurrentUser;
  /** When set (e.g. deep link from Reports), select this campaign once campaigns are loaded. */
  initialCampaignId?: string | null;
  onInitialCampaignConsumed?: () => void;
}

const CouponCampaignsPage: React.FC<CouponCampaignsPageProps> = ({
  currentUser,
  initialCampaignId = null,
  onInitialCampaignConsumed,
}) => {
  const [campaigns, setCampaigns] = useState<CouponCampaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [campaignCoupons, setCampaignCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [campaignKind, setCampaignKind] = useState<CouponCampaignKind>('promotional');
  const [buildingWing, setBuildingWing] = useState<BuildingWing>('hotel_wing');
  const [unitListText, setUnitListText] = useState('');
  const [couponTableFilter, setCouponTableFilter] = useState<'all' | 'never_used'>('all');
  const [totalCoupons, setTotalCoupons] = useState('100');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('10');
  const [expiryDate, setExpiryDate] = useState('');
  const [printCardAspectRatio, setPrintCardAspectRatio] = useState<CouponCardSize>(
    defaultCardSizeForCampaignKind('promotional'),
  );
  const [printSheetRows95x38, setPrintSheetRows95x38] = useState(4);
  const appliedInitialCampaignRef = useRef<string | null>(null);
  const [overlayDraft, setOverlayDraft] = useState<CouponPrintOverlayLayout>(() =>
    defaultPrintOverlayForKind('promotional')
  );
  const [printConditionsText, setPrintConditionsText] = useState('');
  const [dragging, setDragging] = useState<null | 'code' | 'off' | 'expiry' | 'barcode' | 'conditions' | 'unitLabel'>(
    null
  );
  const [templateUploading, setTemplateUploading] = useState(false);
  const previewWrapRef = useRef<HTMLDivElement>(null);
  const templateFileInputRef = useRef<HTMLInputElement>(null);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === selectedCampaignId) ?? null,
    [campaigns, selectedCampaignId]
  );

  const isBuildingCampaign = selectedCampaign?.campaignKind === 'building_unit';

  const resolvedTemplateUrl = useMemo(
    () => resolveCampaignTemplateUrl(selectedCampaign),
    [selectedCampaign]
  );

  const previewCoupon = useMemo(() => {
    if (campaignCoupons.length > 0) return campaignCoupons[0];
    return null;
  }, [campaignCoupons]);

  useEffect(() => {
    const campaign = campaigns.find((c) => c.id === selectedCampaignId) || null;
    if (!campaign) return;
    setOverlayDraft(
      mergeCouponPrintOverlay(campaign.printOverlayLayout, campaign.campaignKind ?? 'promotional')
    );
    const kind = campaign.campaignKind ?? 'promotional';
    const normalizedSize = normalizeCouponCardSize(campaign.printCardAspectRatio, kind);
    setPrintCardAspectRatio(normalizedSize);
    const layoutCfg = getMmSheetLayoutConfig(normalizedSize);
    const savedRows = campaign.printA4SheetRows95x38;
    setPrintSheetRows95x38(
      typeof savedRows === 'number' && Number.isFinite(savedRows) && layoutCfg
        ? clampA4Rows(layoutCfg, Math.round(savedRows))
        : layoutCfg?.defaultRows ?? 4
    );
    setPrintConditionsText(typeof campaign.printConditionsText === 'string' ? campaign.printConditionsText : '');
  }, [campaigns, selectedCampaignId]);

  const mmLayoutConfig = useMemo(
    () => getMmSheetLayoutConfig(printCardAspectRatio),
    [printCardAspectRatio]
  );
  const maxSheetRowsMm = useMemo(
    () => (mmLayoutConfig ? maxA4Rows(mmLayoutConfig) : 0),
    [mmLayoutConfig]
  );
  const sheetRowsMmInvalid = useMemo(() => {
    if (!isMmSheetCardSize(printCardAspectRatio) || !mmLayoutConfig) return false;
    const r = printSheetRows95x38;
    if (!Number.isInteger(r)) return true;
    return r < 1 || r > maxSheetRowsMm;
  }, [printCardAspectRatio, printSheetRows95x38, maxSheetRowsMm, mmLayoutConfig]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => {
      const el = previewWrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pctY = ((e.clientY - rect.top) / rect.height) * 100;
      const pctX = ((e.clientX - rect.left) / rect.width) * 100;
      const fineDrag = dragging === 'barcode' || dragging === 'unitLabel';
      const snap = (v: number) => snapOverlayDragPct(v, fineDrag ? 'fine' : 'coarse');
      const clampedY = snap(pctY);
      const clampedX = snap(pctX);
      setOverlayDraft((d) => {
        if (dragging === 'code') return { ...d, codeTopPct: clampedY, codeLeftPct: clampedX };
        if (dragging === 'off') return { ...d, offTopPct: clampedY, offLeftPct: clampedX };
        if (dragging === 'barcode') return { ...d, barcodeTopPct: clampedY, barcodeLeftPct: clampedX };
        if (dragging === 'conditions') return { ...d, conditionsTopPct: clampedY, conditionsLeftPct: clampedX };
        if (dragging === 'unitLabel') return { ...d, unitLabelTopPct: clampedY, unitLabelLeftPct: clampedX };
        return { ...d, expiryTopPct: clampedY, expiryLeftPct: clampedX };
      });
    };
    const onUp = () => setDragging(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging]);

  const loadCampaigns = async () => {
    setIsLoading(true);
    try {
      const data = await couponCampaignsService.getAll();
      setCampaigns(data);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCouponsForCampaign = async (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    const coupons = await couponsService.getByCampaignId(campaignId);
    setCampaignCoupons(coupons);
  };

  const parsedUnitPreview = useMemo(() => {
    if (campaignKind !== 'building_unit' || !unitListText.trim()) return [];
    try {
      return parseBuildingUnitList(unitListText, buildingWing);
    } catch {
      return [];
    }
  }, [campaignKind, unitListText, buildingWing]);

  const handleUnitCsvUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setUnitListText((prev) => (prev.trim() ? `${prev}\n${text}` : text));
    };
    reader.readAsText(file);
  };

  const handleTemplateImageUpload = async (file: File) => {
    if (!selectedCampaign) return;
    setTemplateUploading(true);
    try {
      const { url, pdfDataUrl } = await uploadCouponCampaignTemplateImage(file, selectedCampaign.id);
      await couponCampaignsService.update(selectedCampaign.id, {
        printTemplateImageUrl: url,
        printTemplateImageDataUrl: pdfDataUrl,
      });
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === selectedCampaign.id
            ? { ...c, printTemplateImageUrl: url, printTemplateImageDataUrl: pdfDataUrl }
            : c
        )
      );
    } catch (err) {
      alert((err as Error).message || 'Could not upload template image.');
    } finally {
      setTemplateUploading(false);
    }
  };

  const handleRemoveTemplateImage = async () => {
    if (!selectedCampaign) return;
    const confirmed = window.confirm('Remove the uploaded card design? The default template for this campaign type will be used.');
    if (!confirmed) return;
    setTemplateUploading(true);
    try {
      await couponCampaignsService.update(selectedCampaign.id, {
        printTemplateImageUrl: null,
        printTemplateImageDataUrl: null,
      });
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === selectedCampaign.id
            ? { ...c, printTemplateImageUrl: null, printTemplateImageDataUrl: null }
            : c
        )
      );
    } finally {
      setTemplateUploading(false);
    }
  };

  const createCampaign = async () => {
    if (!name.trim()) {
      alert('Campaign name is required.');
      return;
    }
    const value = Number(discountValue);
    if (!Number.isFinite(value) || value <= 0) {
      alert('Please provide a valid discount value.');
      return;
    }
    setIsLoading(true);
    try {
      if (campaignKind === 'building_unit') {
        let units: string[];
        try {
          units = parseBuildingUnitList(unitListText, buildingWing);
        } catch (err) {
          alert((err as Error).message || 'Could not parse unit list.');
          return;
        }
        const created = await couponCampaignsService.create({
          name: name.trim(),
          totalCoupons: units.length,
          campaignKind: 'building_unit',
          buildingWing,
          status: 'published',
          printCardAspectRatio,
          ...(isMmSheetCardSize(printCardAspectRatio)
            ? {
                printA4SheetRows95x38: clampA4Rows(
                  getMmSheetLayoutConfig(printCardAspectRatio)!,
                  printSheetRows95x38
                ),
              }
            : {}),
          discountType,
          discountValue: value,
          expiryDate: null,
          createdByStaffId: currentUser.id,
        });
        await couponsService.createBatchFromUnitList(created.id, units, { buildingWing });
        await loadCampaigns();
        await loadCouponsForCampaign(created.id);
        setName('');
        setUnitListText('');
        return;
      }
      const total = Number(totalCoupons);
      if (!Number.isFinite(total) || total <= 0) {
        alert('Please provide a valid coupon count.');
        return;
      }
      const created = await couponCampaignsService.create({
        name: name.trim(),
        totalCoupons: total,
        campaignKind: 'promotional',
        status: 'draft',
        printCardAspectRatio,
        ...(isMmSheetCardSize(printCardAspectRatio)
          ? {
              printA4SheetRows95x38: clampA4Rows(
                getMmSheetLayoutConfig(printCardAspectRatio)!,
                printSheetRows95x38
              ),
            }
          : {}),
        discountType,
        discountValue: value,
        expiryDate: expiryDate ? localEndOfDayFromDateInput(expiryDate) : null,
        createdByStaffId: currentUser.id,
      });
      await couponsService.createBatchFromCampaign(created.id, {
        endDate: created.expiryDate ?? null,
      });
      await loadCampaigns();
      await loadCouponsForCampaign(created.id);
      setName('');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTextFile = (filename: string, data: string, mimeType: string) => {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const displayedCoupons = useMemo(() => {
    if (couponTableFilter !== 'never_used') return campaignCoupons;
    return campaignCoupons.filter((c) => (c.usedCount ?? 0) === 0);
  }, [campaignCoupons, couponTableFilter]);

  const exportCSV = () => {
    if (!selectedCampaign) return;
    downloadTextFile(
      `${selectedCampaign.name.replace(/\s+/g, '_').toLowerCase()}_coupons.csv`,
      couponsToCSV(campaignCoupons, { buildingUnit: isBuildingCampaign }),
      'text/csv;charset=utf-8'
    );
  };

  const exportJSON = () => {
    if (!selectedCampaign) return;
    downloadTextFile(
      `${selectedCampaign.name.replace(/\s+/g, '_').toLowerCase()}_coupons.json`,
      couponsToJSON(campaignCoupons),
      'application/json;charset=utf-8'
    );
  };

  const exportPDF = async () => {
    if (!selectedCampaign) return;
    if (campaignCoupons.length === 0) {
      alert('No coupons to export for this campaign.');
      return;
    }
    setIsLoading(true);
    try {
      await exportCouponsPdfWithBarcodes(selectedCampaign.name, campaignCoupons);
    } finally {
      setIsLoading(false);
    }
  };

  const saveCouponsA4Pdf = async () => {
    if (!selectedCampaign) return;
    if (campaignCoupons.length === 0) {
      alert('No coupons to export for this campaign.');
      return;
    }
    if (isMmSheetCardSize(printCardAspectRatio) && sheetRowsMmInvalid) {
      alert(
        `This row count does not fit on A4 at the selected card size. Use between 1 and ${maxSheetRowsMm} rows (current sheet would be about ${gridHeightMm(mmLayoutConfig!, printSheetRows95x38).toFixed(0)} mm tall before margins).`
      );
      return;
    }
    const templateImageUrl = resolveCampaignTemplateUrl(selectedCampaign) || undefined;
    if (!templateImageUrl) {
      alert('Upload a card design image in the template panel before saving the PDF.');
      return;
    }
    setIsLoading(true);
    try {
      await exportCouponsA4SheetPdf(selectedCampaign.name, campaignCoupons, {
        templateImageUrl,
        templateImageDataUrl: resolveCampaignTemplateDataUrl(selectedCampaign),
        cardAspectRatio: printCardAspectRatio,
        a4SheetRows95x38: isMmSheetCardSize(printCardAspectRatio) ? printSheetRows95x38 : undefined,
        overlayLayout: mergeCouponPrintOverlay(overlayDraft, selectedCampaign.campaignKind ?? 'promotional'),
        conditionsText: printConditionsText.trim() || undefined,
        buildingUnit: selectedCampaign.campaignKind === 'building_unit',
      });
      if (!resolveCampaignTemplateDataUrl(selectedCampaign)) {
        void tryCreatePdfCacheDataUrl(templateImageUrl).then((pdfDataUrl) => {
          if (!pdfDataUrl || !selectedCampaign) return;
          void couponCampaignsService.update(selectedCampaign.id, { printTemplateImageDataUrl: pdfDataUrl });
          setCampaigns((prev) =>
            prev.map((c) =>
              c.id === selectedCampaign.id ? { ...c, printTemplateImageDataUrl: pdfDataUrl } : c
            )
          );
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate PDF.';
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveOverlayLayout = async () => {
    if (!selectedCampaign) return;
    if (isMmSheetCardSize(printCardAspectRatio) && sheetRowsMmInvalid) {
      alert(
        `Save blocked: choose between 1 and ${maxSheetRowsMm} rows per A4 sheet for this card size.`
      );
      return;
    }
    setIsLoading(true);
    try {
      const merged = mergeCouponPrintOverlay(overlayDraft, selectedCampaign.campaignKind ?? 'promotional');
      const rowsPayload =
        isMmSheetCardSize(printCardAspectRatio) && mmLayoutConfig
          ? { printA4SheetRows95x38: clampA4Rows(mmLayoutConfig, printSheetRows95x38) }
          : {};
      const trimmedConditions = printConditionsText.trim();
      await couponCampaignsService.update(selectedCampaign.id, {
        printOverlayLayout: merged,
        printCardAspectRatio,
        printConditionsText: trimmedConditions.length > 0 ? trimmedConditions : null,
        ...rowsPayload,
      });
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === selectedCampaign.id
            ? {
                ...c,
                printOverlayLayout: merged,
                printCardAspectRatio,
                printConditionsText: trimmedConditions.length > 0 ? trimmedConditions : null,
                ...rowsPayload,
              }
            : c
        )
      );
      alert('Template text layout saved.');
    } catch {
      alert('Could not save print layout. Check your connection and permissions.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateCampaignStatus = async (nextStatus: 'draft' | 'published' | 'cancelled') => {
    if (!selectedCampaign) return;
    setIsLoading(true);
    try {
      await couponCampaignsService.update(selectedCampaign.id, { status: nextStatus });
      setCampaigns((prev) => prev.map((c) => (c.id === selectedCampaign.id ? { ...c, status: nextStatus } : c)));
    } finally {
      setIsLoading(false);
    }
  };

  const removeCampaign = async () => {
    if (!selectedCampaign) return;
    const confirmed = window.confirm(`Delete campaign "${selectedCampaign.name}" and all of its coupons?`);
    if (!confirmed) return;
    setIsLoading(true);
    try {
      const coupons = await couponsService.getByCampaignId(selectedCampaign.id);
      await Promise.all(coupons.map((coupon) => couponsService.delete(coupon.id)));
      await couponCampaignsService.delete(selectedCampaign.id);
      setCampaigns((prev) => prev.filter((c) => c.id !== selectedCampaign.id));
      setSelectedCampaignId('');
      setCampaignCoupons([]);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    void loadCampaigns();
  }, []);

  React.useEffect(() => {
    if (!initialCampaignId) {
      appliedInitialCampaignRef.current = null;
      return;
    }
    if (campaigns.length === 0) return;
    if (appliedInitialCampaignRef.current === initialCampaignId) return;
    const exists = campaigns.some((c) => c.id === initialCampaignId);
    if (!exists) {
      onInitialCampaignConsumed?.();
      return;
    }
    appliedInitialCampaignRef.current = initialCampaignId;
    void (async () => {
      setSelectedCampaignId(initialCampaignId);
      const coupons = await couponsService.getByCampaignId(initialCampaignId);
      setCampaignCoupons(coupons);
      onInitialCampaignConsumed?.();
    })();
  }, [initialCampaignId, campaigns, onInitialCampaignConsumed]);

  return (
    <div className="h-full overflow-y-auto p-6 pb-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Coupon Campaign Management</h1>
        <p className="text-sm text-gray-600">
          Create campaigns, generate unique coupon batches, and export/print them. Building unit cards use a separate design and template from promotional coupons.
        </p>
      </div>

      <section className="p-4 bg-white rounded-lg border border-gray-200 space-y-3">
        <h2 className="text-lg font-semibold">Create Campaign</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setCampaignKind('promotional');
              setPrintCardAspectRatio(defaultCardSizeForCampaignKind('promotional'));
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${campaignKind === 'promotional' ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Promotional coupons
          </button>
          <button
            type="button"
            onClick={() => {
              setCampaignKind('building_unit');
              setPrintCardAspectRatio(defaultCardSizeForCampaignKind('building_unit'));
              const cfg = getMmSheetLayoutConfig('86/54');
              if (cfg) setPrintSheetRows95x38(cfg.defaultRows);
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${campaignKind === 'building_unit' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Building unit cards
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)} className="px-3 py-2 border rounded-md" placeholder="Campaign name" />
          {campaignKind === 'promotional' ? (
            <input value={totalCoupons} onChange={(e) => setTotalCoupons(e.target.value)} className="px-3 py-2 border rounded-md" placeholder="Total coupons" />
          ) : (
            <select
              value={buildingWing}
              onChange={(e) => setBuildingWing(e.target.value as BuildingWing)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="hotel_wing">{BUILDING_WING_LABEL.hotel_wing} (HW-)</option>
              <option value="tower_wing">{BUILDING_WING_LABEL.tower_wing} (TW-)</option>
            </select>
          )}
          <select value={discountType} onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')} className="px-3 py-2 border rounded-md">
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed</option>
          </select>
          <input value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="px-3 py-2 border rounded-md" placeholder="Discount value" />
          {campaignKind === 'promotional' ? (
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="px-3 py-2 border rounded-md" />
          ) : (
            <div className="px-3 py-2 border rounded-md bg-gray-50 text-sm text-gray-600 flex items-center">
              {parsedUnitPreview.length > 0 ? `Will create ${parsedUnitPreview.length} cards` : 'Paste units below'}
            </div>
          )}
        </div>
        {campaignKind === 'building_unit' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Unit numbers (one per line — bare numbers get {buildingWing === 'hotel_wing' ? 'HW-' : 'TW-'} prefix)
            </label>
            <textarea
              value={unitListText}
              onChange={(e) => setUnitListText(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border rounded-md font-mono text-sm"
              placeholder={'101\n102\n201\n7B\n(or HW-101 if already prefixed)'}
            />
            {parsedUnitPreview.length > 0 && (
              <p className="text-xs text-gray-500">
                Preview: {parsedUnitPreview.slice(0, 5).join(', ')}
                {parsedUnitPreview.length > 5 ? ` … +${parsedUnitPreview.length - 5} more` : ''}
              </p>
            )}
            <input
              type="file"
              accept=".csv,.txt"
              className="text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUnitCsvUpload(file);
                e.target.value = '';
              }}
            />
          </div>
        )}
        <button onClick={createCampaign} disabled={isLoading} className="px-4 py-2 rounded-md bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-60">
          {isLoading
            ? 'Working...'
            : campaignKind === 'building_unit'
              ? 'Create & Generate Unit Cards'
              : 'Create & Generate Coupons'}
        </button>
      </section>

      {selectedCampaign && (
        <section
          className={`p-4 rounded-lg border space-y-4 ${
            isBuildingCampaign ? 'bg-indigo-50/40 border-indigo-200' : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isBuildingCampaign ? 'Building unit card design' : 'Promotional coupon design'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isBuildingCampaign
                  ? 'Upload a resident-card background (separate from promotional coupons). Position unit number, discount, barcode, and terms on the preview, then save and print.'
                  : 'Upload a coupon background or use the site default. Position text and barcode on the preview, then save so A4 print matches.'}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  ref={templateFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleTemplateImageUpload(file);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  disabled={templateUploading}
                  onClick={() => templateFileInputRef.current?.click()}
                  className={`px-3 py-1.5 text-sm rounded-md text-white disabled:opacity-50 ${
                    isBuildingCampaign ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-pink-600 hover:bg-pink-700'
                  }`}
                >
                  {templateUploading ? 'Uploading…' : 'Upload card image'}
                </button>
                {selectedCampaign.printTemplateImageUrl && (
                  <button
                    type="button"
                    disabled={templateUploading}
                    onClick={() => void handleRemoveTemplateImage()}
                    className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-white disabled:opacity-50"
                  >
                    Remove upload
                  </button>
                )}
                <span className="text-xs text-gray-500">
                  {selectedCampaign.printTemplateImageUrl
                    ? 'Using this campaign’s uploaded image'
                    : resolvedTemplateUrl
                      ? 'Using site default template'
                      : 'No template yet — upload an image to enable preview & print'}
                </span>
              </div>
              <label className="block mt-3 text-sm text-gray-700">
                <span className="font-medium text-gray-800">Terms / conditions (optional)</span>
                <textarea
                  value={printConditionsText}
                  onChange={(e) => setPrintConditionsText(e.target.value)}
                  rows={4}
                  placeholder="e.g. Not valid with other offers. Dine-in only."
                  className="mt-1 w-full max-w-xl px-3 py-2 border border-gray-300 rounded-md text-sm font-normal"
                />
              </label>
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Show on card</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                  {isBuildingCampaign && (
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overlayDraft.showUnitLabel !== false}
                        onChange={(e) =>
                          setOverlayDraft((d) => ({ ...d, showUnitLabel: e.target.checked }))
                        }
                        className="rounded border-gray-300"
                      />
                      Unit number
                    </label>
                  )}
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayDraft.showCode !== false}
                      onChange={(e) => setOverlayDraft((d) => ({ ...d, showCode: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    {isBuildingCampaign ? 'Card code' : 'Coupon code'}
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayDraft.showDiscount !== false}
                      onChange={(e) =>
                        setOverlayDraft((d) => ({ ...d, showDiscount: e.target.checked }))
                      }
                      className="rounded border-gray-300"
                    />
                    Discount
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayDraft.showExpiry !== false}
                      onChange={(e) =>
                        setOverlayDraft((d) => ({ ...d, showExpiry: e.target.checked }))
                      }
                      className="rounded border-gray-300"
                    />
                    {isBuildingCampaign ? 'Permanent discount line' : 'Expiry'}
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayDraft.showBarcode !== false}
                      onChange={(e) =>
                        setOverlayDraft((d) => ({ ...d, showBarcode: e.target.checked }))
                      }
                      className="rounded border-gray-300"
                    />
                    Barcode
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayDraft.showConditions !== false}
                      onChange={(e) =>
                        setOverlayDraft((d) => ({ ...d, showConditions: e.target.checked }))
                      }
                      className="rounded border-gray-300"
                    />
                    Terms / conditions
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Uncheck items you do not want on the printed card. Save print layout after changing.
                </p>
              </div>
              <div className="mt-2 max-w-[220px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Card size</label>
                <select
                  value={printCardAspectRatio}
                  onChange={(e) => {
                    const next = e.target.value as CouponCardSize;
                    setPrintCardAspectRatio(next);
                    const cfg = getMmSheetLayoutConfig(next);
                    if (cfg) setPrintSheetRows95x38(cfg.defaultRows);
                  }}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  {cardSizesForCampaignKind(selectedCampaign?.campaignKind ?? campaignKind).map((size) => (
                    <option key={size} value={size}>
                      {COUPON_CARD_SIZE_LABEL[size]} — two per row
                    </option>
                  ))}
                </select>
              </div>
              {isMmSheetCardSize(printCardAspectRatio) && (
                <div className="mt-3 max-w-xs space-y-1">
                  <label className="block text-xs font-medium text-gray-600" htmlFor="coupon-sheet-rows">
                    Rows per A4 sheet
                  </label>
                  <input
                    id="coupon-sheet-rows"
                    type="number"
                    min={1}
                    max={maxSheetRowsMm}
                    step={1}
                    value={printSheetRows95x38}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setPrintSheetRows95x38(Number.isFinite(n) ? Math.trunc(n) : mmLayoutConfig?.defaultRows ?? 4);
                    }}
                    onBlur={() => mmLayoutConfig && setPrintSheetRows95x38((r) => clampA4Rows(mmLayoutConfig, r))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    Card size stays fixed at the dimensions above. Up to {maxSheetRowsMm} rows fit in the printable area (~
                    {printableHeightMmA4().toFixed(1)} mm tall). Each sheet holds{' '}
                    {sheetRowsMmInvalid ? '—' : couponsPerSheet(mmLayoutConfig!, printSheetRows95x38)} coupons (2 per row).
                  </p>
                  {sheetRowsMmInvalid && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      This row count cannot be accommodated on one A4 page at the current margins (grid would be about{' '}
                      {gridHeightMm(mmLayoutConfig!, printSheetRows95x38).toFixed(0)} mm tall). Use 1–{maxSheetRowsMm} rows to
                      save or print.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                disabled={isLoading || sheetRowsMmInvalid}
                className={`px-4 py-2 rounded-md text-white disabled:opacity-50 text-sm ${
                  isBuildingCampaign ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-pink-600 hover:bg-pink-700'
                }`}
                onClick={saveOverlayLayout}
              >
                Save template position
              </button>
              <button
                type="button"
                onClick={() => void saveCouponsA4Pdf()}
                disabled={isLoading || campaignCoupons.length === 0 || sheetRowsMmInvalid}
                className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 text-sm"
                title={campaignCoupons.length === 0 ? 'Select a campaign with generated coupons to export.' : 'Save A4 coupon sheet PDF'}
              >
                {isLoading ? 'Saving PDF…' : 'Save A4 PDF'}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50"
                onClick={() =>
                  setOverlayDraft(
                    defaultPrintOverlayForKind(selectedCampaign?.campaignKind ?? 'promotional')
                  )
                }
              >
                Reset
              </button>
            </div>
          </div>
          {resolvedTemplateUrl ? (
            <div
              ref={previewWrapRef}
              className="relative mx-auto max-w-xs sm:max-w-sm bg-neutral-200 rounded-lg overflow-hidden select-none touch-none"
              style={{
                aspectRatio: cssAspectRatioForCardSize(printCardAspectRatio),
              }}
            >
              <img src={resolvedTemplateUrl} alt="" className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none" draggable={false} />
            {(() => {
              const c = previewCoupon;
              const code = c?.code ?? 'KP-PREVIEW-XXXX';
              const off =
                c?.discountType === 'percentage'
                  ? `${c.discountValue}% OFF`
                  : `$${Number(c?.discountValue ?? selectedCampaign.discountValue).toFixed(2)} OFF`;
              const exp = isBuildingCampaign
                ? 'Permanent resident discount'
                : c?.endDate
                  ? `Expiry: ${new Date(c.endDate).toLocaleDateString('en-NZ')}`
                  : selectedCampaign.expiryDate
                    ? `Expiry: ${new Date(selectedCampaign.expiryDate).toLocaleDateString('en-NZ')}`
                    : 'Expiry: —';
              const wing = selectedCampaign.buildingWing ?? buildingWing;
              const unitPreview =
                c?.unitLabel?.trim() ||
                (isBuildingCampaign ? (wing === 'hotel_wing' ? 'HW-101' : 'TW-101') : '');
              const barcodeText = getCouponBarcodeValue(code, isBuildingCampaign);
              const row = (
                field: 'code' | 'off' | 'expiry',
                label: string,
                text: string,
                leftPct: number,
                topPct: number,
                fontRem: number | undefined,
                weight: number
              ) => (
                <div
                  key={field}
                  role="button"
                  tabIndex={0}
                  className={`absolute w-[88%] max-w-[88%] left-1/2 -translate-x-1/2 text-center font-semibold px-1 overflow-hidden text-ellipsis whitespace-nowrap ${
                    dragging === field ? 'cursor-grabbing ring-2 ring-pink-500 ring-offset-1 rounded' : 'cursor-grab'
                  }`}
                  style={{
                    left: `${leftPct}%`,
                    top: `${topPct}%`,
                    transform: 'translate(-50%, -50%)',
                    color: overlayDraft.textColor ?? '#ffffff',
                    fontSize: `${fontRem ?? 1}rem`,
                    fontWeight: weight,
                    zIndex: field === 'code' ? 3 : field === 'off' ? 4 : 5,
                  }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    setDragging(field);
                  }}
                  title={label}
                >
                  {text}
                </div>
              );
              return (
                <>
                  {isBuildingCampaign &&
                    unitPreview.length > 0 &&
                    isPrintOverlayVisible(overlayDraft, 'unitLabel') && (
                    <div
                      role="button"
                      tabIndex={0}
                      className={`absolute w-[88%] max-w-[88%] left-1/2 -translate-x-1/2 text-center font-bold px-1 overflow-hidden text-ellipsis whitespace-nowrap ${
                        dragging === 'unitLabel' ? 'cursor-grabbing ring-2 ring-indigo-500 ring-offset-1 rounded' : 'cursor-grab'
                      }`}
                      style={{
                        left: `${overlayDraft.unitLabelLeftPct ?? 50}%`,
                        top: `${overlayDraft.unitLabelTopPct ?? 42}%`,
                        transform: 'translate(-50%, -50%)',
                        color: overlayDraft.textColor ?? '#0f172a',
                        fontSize: `${overlayDraft.unitLabelFontRem ?? 1.35}rem`,
                        zIndex: 2,
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        setDragging('unitLabel');
                      }}
                      title="Unit number"
                    >
                      {unitPreview}
                    </div>
                  )}
                  {isPrintOverlayVisible(overlayDraft, 'code') &&
                    row(
                      'code',
                      isBuildingCampaign ? 'Card code' : 'Coupon code',
                      code,
                      overlayDraft.codeLeftPct,
                      overlayDraft.codeTopPct,
                      overlayDraft.codeFontRem,
                      800
                    )}
                  {isPrintOverlayVisible(overlayDraft, 'discount') &&
                    row('off', 'Discount', off, overlayDraft.offLeftPct, overlayDraft.offTopPct, overlayDraft.offFontRem, 900)}
                  {isPrintOverlayVisible(overlayDraft, 'expiry') &&
                    row('expiry', 'Expiry', exp, overlayDraft.expiryLeftPct, overlayDraft.expiryTopPct, overlayDraft.expiryFontRem, 500)}
                  {isPrintOverlayVisible(overlayDraft, 'conditions') && printConditionsText.trim().length > 0 && (
                    <div
                      role="button"
                      tabIndex={0}
                      className={`absolute text-center font-medium px-1 ${
                        dragging === 'conditions'
                          ? 'cursor-grabbing ring-2 ring-pink-500 ring-offset-1 rounded'
                          : 'cursor-grab'
                      }`}
                      style={{
                        left: `${overlayDraft.conditionsLeftPct ?? 50}%`,
                        top: `${overlayDraft.conditionsTopPct ?? 92}%`,
                        width: `${overlayDraft.conditionsWidthPct ?? 88}%`,
                        transform: `translate(-50%, -50%) rotate(${overlayDraft.conditionsRotateDeg ?? 0}deg)`,
                        transformOrigin: 'center center',
                        color: overlayDraft.textColor ?? '#ffffff',
                        fontSize: `${overlayDraft.conditionsFontRem ?? 0.48}rem`,
                        fontWeight: 500,
                        zIndex: 7,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.25,
                        maxHeight: '42%',
                        overflow: 'hidden',
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        setDragging('conditions');
                      }}
                      title="Conditions (drag to position; rotation matches print)"
                    >
                      {printConditionsText.trim()}
                    </div>
                  )}
                  {isPrintOverlayVisible(overlayDraft, 'barcode') && (
                    <div
                      role="button"
                      tabIndex={0}
                      className={`absolute rounded ${
                        dragging === 'barcode'
                          ? 'cursor-grabbing ring-2 ring-pink-500 ring-offset-1'
                          : 'cursor-grab ring-1 ring-transparent hover:ring-pink-300/60'
                      }`}
                      style={{
                        left: `${overlayDraft.barcodeLeftPct}%`,
                        top: `${overlayDraft.barcodeTopPct}%`,
                        transform: `translate(-50%, -50%) rotate(${overlayDraft.barcodeRotateDeg ?? 0}deg)`,
                        transformOrigin: 'center center',
                        width: `${overlayDraft.barcodeWidthPct}%`,
                        height: `${overlayDraft.barcodeHeightPct}%`,
                        zIndex: 6,
                        overflow: 'visible',
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        setDragging('barcode');
                      }}
                      title="Barcode (drag to position; rotation matches print)"
                    >
                      <div
                        className="w-full h-[78%] mx-auto"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(90deg, #111827 0 1px, transparent 1px 2px, #111827 2px 4px, transparent 4px 5px)',
                        }}
                      />
                    </div>
                  )}
                </>
              );
            })()}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center text-sm text-gray-600 max-w-md mx-auto">
              Upload a card image above to preview overlay positions and print A4 sheets.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {isBuildingCampaign && (
              <>
                <label className="flex flex-col gap-1">
                  <span className="text-gray-600">Unit left %</span>
                  <input
                    type="number"
                    step={0.1}
                    className="border rounded px-2 py-1"
                    value={overlayDraft.unitLabelLeftPct ?? 50}
                    onChange={(e) =>
                      setOverlayDraft((d) => ({
                        ...d,
                        unitLabelLeftPct: snapOverlayDragPct(Number(e.target.value), 'fine'),
                      }))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-gray-600">Unit top %</span>
                  <input
                    type="number"
                    step={0.1}
                    className="border rounded px-2 py-1"
                    value={overlayDraft.unitLabelTopPct ?? 42}
                    onChange={(e) =>
                      setOverlayDraft((d) => ({
                        ...d,
                        unitLabelTopPct: snapOverlayDragPct(Number(e.target.value), 'fine'),
                      }))
                    }
                  />
                </label>
              </>
            )}
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Code left %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.codeLeftPct}
                onChange={(e) => setOverlayDraft((d) => ({ ...d, codeLeftPct: Math.round(Number(e.target.value)) }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Code top %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.codeTopPct}
                onChange={(e) => setOverlayDraft((d) => ({ ...d, codeTopPct: Math.round(Number(e.target.value)) }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Discount left %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.offLeftPct}
                onChange={(e) => setOverlayDraft((d) => ({ ...d, offLeftPct: Math.round(Number(e.target.value)) }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Discount top %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.offTopPct}
                onChange={(e) => setOverlayDraft((d) => ({ ...d, offTopPct: Math.round(Number(e.target.value)) }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Expiry left %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.expiryLeftPct}
                onChange={(e) => setOverlayDraft((d) => ({ ...d, expiryLeftPct: Math.round(Number(e.target.value)) }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Expiry top %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.expiryTopPct}
                onChange={(e) => setOverlayDraft((d) => ({ ...d, expiryTopPct: Math.round(Number(e.target.value)) }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Text colour</span>
              <input
                type="color"
                className="h-9 w-full border rounded cursor-pointer"
                value={/^#[0-9a-fA-F]{6}$/.test(overlayDraft.textColor ?? '') ? overlayDraft.textColor : '#ffffff'}
                onChange={(e) => setOverlayDraft((d) => ({ ...d, textColor: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Barcode left %</span>
              <input
                type="number"
                step={0.1}
                className="border rounded px-2 py-1"
                value={overlayDraft.barcodeLeftPct}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    barcodeLeftPct: snapOverlayDragPct(Number(e.target.value), 'fine'),
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Barcode top %</span>
              <input
                type="number"
                step={0.1}
                className="border rounded px-2 py-1"
                value={overlayDraft.barcodeTopPct}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    barcodeTopPct: snapOverlayDragPct(Number(e.target.value), 'fine'),
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Barcode width %</span>
              <input
                type="number"
                step={0.5}
                min={20}
                max={95}
                className="border rounded px-2 py-1"
                value={overlayDraft.barcodeWidthPct}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    barcodeWidthPct: Math.round(Number(e.target.value) * 2) / 2,
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Barcode height %</span>
              <input
                type="number"
                step={0.5}
                min={6}
                max={30}
                className="border rounded px-2 py-1"
                value={overlayDraft.barcodeHeightPct}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    barcodeHeightPct: Math.round(Number(e.target.value) * 2) / 2,
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Barcode rotate (deg)</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  step={1}
                  min={-180}
                  max={180}
                  className="border rounded px-2 py-1 w-20"
                  value={overlayDraft.barcodeRotateDeg ?? 0}
                  onChange={(e) => setOverlayDraft((d) => ({ ...d, barcodeRotateDeg: Math.round(Number(e.target.value) || 0) }))}
                />
                {[0, 90, -90].map((deg) => (
                  <button
                    key={deg}
                    type="button"
                    className={`px-2 py-1 text-xs rounded border ${
                      (overlayDraft.barcodeRotateDeg ?? 0) === deg
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setOverlayDraft((d) => ({ ...d, barcodeRotateDeg: deg }))}
                  >
                    {deg}°
                  </button>
                ))}
              </div>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Conditions left %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.conditionsLeftPct ?? 50}
                onChange={(e) =>
                  setOverlayDraft((d) => ({ ...d, conditionsLeftPct: Math.round(Number(e.target.value)) }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Conditions top %</span>
              <input
                type="number"
                step={1}
                className="border rounded px-2 py-1"
                value={overlayDraft.conditionsTopPct ?? 92}
                onChange={(e) =>
                  setOverlayDraft((d) => ({ ...d, conditionsTopPct: Math.round(Number(e.target.value)) }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Conditions width %</span>
              <input
                type="number"
                step={1}
                min={30}
                max={96}
                className="border rounded px-2 py-1"
                value={overlayDraft.conditionsWidthPct ?? 88}
                onChange={(e) =>
                  setOverlayDraft((d) => ({ ...d, conditionsWidthPct: Math.round(Number(e.target.value)) }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Conditions size (rem)</span>
              <input
                type="number"
                step={0.01}
                min={0.35}
                max={4}
                className="border rounded px-2 py-1"
                value={overlayDraft.conditionsFontRem ?? 0.48}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    conditionsFontRem: Number((Math.round(Number(e.target.value) * 100) / 100).toFixed(2)),
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Conditions rotate (deg)</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                <input
                  type="number"
                  step={1}
                  min={-180}
                  max={180}
                  className="border rounded px-2 py-1 w-20"
                  value={overlayDraft.conditionsRotateDeg ?? 0}
                  onChange={(e) =>
                    setOverlayDraft((d) => ({
                      ...d,
                      conditionsRotateDeg: Math.round(Number(e.target.value) || 0),
                    }))
                  }
                />
                {[0, 90, -90].map((deg) => (
                  <button
                    key={`c-${deg}`}
                    type="button"
                    className={`px-2 py-1 text-xs rounded border ${
                      (overlayDraft.conditionsRotateDeg ?? 0) === deg
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setOverlayDraft((d) => ({ ...d, conditionsRotateDeg: deg }))}
                  >
                    {deg}°
                  </button>
                ))}
              </div>
            </label>
            {isBuildingCampaign && (
              <label className="flex flex-col gap-1">
                <span className="text-gray-600">Unit size (rem)</span>
                <input
                  type="number"
                  step={0.01}
                  min={0.4}
                  max={4}
                  className="border rounded px-2 py-1"
                  value={overlayDraft.unitLabelFontRem ?? 1.35}
                  onChange={(e) =>
                    setOverlayDraft((d) => ({
                      ...d,
                      unitLabelFontRem: Number((Math.round(Number(e.target.value) * 100) / 100).toFixed(2)),
                    }))
                  }
                />
              </label>
            )}
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Code size (rem)</span>
              <input
                type="number"
                step={0.01}
                min={0.4}
                max={4}
                className="border rounded px-2 py-1"
                value={overlayDraft.codeFontRem}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    codeFontRem: Number((Math.round(Number(e.target.value) * 100) / 100).toFixed(2)),
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Discount size (rem)</span>
              <input
                type="number"
                step={0.01}
                min={0.4}
                max={4}
                className="border rounded px-2 py-1"
                value={overlayDraft.offFontRem}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    offFontRem: Number((Math.round(Number(e.target.value) * 100) / 100).toFixed(2)),
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Expiry size (rem)</span>
              <input
                type="number"
                step={0.01}
                min={0.4}
                max={4}
                className="border rounded px-2 py-1"
                value={overlayDraft.expiryFontRem}
                onChange={(e) =>
                  setOverlayDraft((d) => ({
                    ...d,
                    expiryFontRem: Number((Math.round(Number(e.target.value) * 100) / 100).toFixed(2)),
                  }))
                }
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={isLoading}
              className="px-4 py-2 rounded-md bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50 text-sm"
              onClick={saveOverlayLayout}
            >
              Save print layout to campaign
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50"
              onClick={() =>
                setOverlayDraft({
                  ...defaultPrintOverlayForKind(selectedCampaign?.campaignKind ?? 'promotional'),
                })
              }
            >
              Reset to defaults
            </button>
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h2 className="text-lg font-semibold mb-3">Campaigns</h2>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {campaigns.map((campaign) => (
              <button
                key={campaign.id}
                onClick={() => loadCouponsForCampaign(campaign.id)}
                className={`w-full text-left p-3 border rounded-md ${selectedCampaignId === campaign.id ? 'border-pink-500 bg-pink-50' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold">{campaign.name}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 mr-1">
                    {(campaign.campaignKind ?? 'promotional') === 'building_unit' ? 'Building' : 'Promo'}
                  </span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full ${
                      (campaign.status ?? 'draft') === 'published'
                        ? 'bg-green-100 text-green-700'
                        : (campaign.status ?? 'draft') === 'cancelled'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {(campaign.status ?? 'draft').toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-600">
                  {campaign.totalCoupons} {campaign.campaignKind === 'building_unit' ? 'unit cards' : 'coupons'}
                  {campaign.buildingWing
                    ? ` · ${BUILDING_WING_LABEL[campaign.buildingWing]}`
                    : campaign.unitType === 'hotel'
                      ? ` · ${BUILDING_WING_LABEL.hotel_wing}`
                      : campaign.unitType === 'apartment'
                        ? ` · ${BUILDING_WING_LABEL.tower_wing}`
                        : ''}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-lg font-semibold">
              Generated Coupons {selectedCampaign ? `(${(selectedCampaign.status ?? 'draft').toUpperCase()})` : ''}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => void updateCampaignStatus('draft')}
                disabled={!selectedCampaign || isLoading}
                className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
              >
                Mark Draft
              </button>
              <button
                onClick={() => void updateCampaignStatus('published')}
                disabled={!selectedCampaign || isLoading}
                className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
              >
                Publish
              </button>
              <button
                onClick={() => void updateCampaignStatus('cancelled')}
                disabled={!selectedCampaign || isLoading}
                className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void removeCampaign()}
                disabled={!selectedCampaign || isLoading}
                className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-md disabled:opacity-50"
              >
                Remove Campaign
              </button>
              <button onClick={exportCSV} disabled={!selectedCampaign} className="px-3 py-1.5 text-sm border rounded-md">Export CSV (with barcode value)</button>
              <button onClick={exportJSON} disabled={!selectedCampaign} className="px-3 py-1.5 text-sm border rounded-md">Export JSON</button>
              <button
                onClick={() => void exportPDF()}
                disabled={!selectedCampaign || campaignCoupons.length === 0 || isLoading}
                className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
              >
                Export PDF (Code128)
              </button>
              <button
                onClick={() => void saveCouponsA4Pdf()}
                disabled={!selectedCampaign || campaignCoupons.length === 0 || isLoading}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md disabled:opacity-50"
              >
                {isLoading ? 'Saving PDF…' : 'Save A4 PDF'}
              </button>
            </div>
          </div>
          {isBuildingCampaign && (
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setCouponTableFilter('all')} className={`px-2 py-1 text-xs rounded border ${couponTableFilter === 'all' ? 'bg-indigo-100 border-indigo-300' : ''}`}>All</button>
              <button type="button" onClick={() => setCouponTableFilter('never_used')} className={`px-2 py-1 text-xs rounded border ${couponTableFilter === 'never_used' ? 'bg-indigo-100 border-indigo-300' : ''}`}>Never used</button>
            </div>
          )}
          <div className="max-h-[420px] overflow-y-auto border rounded-md">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {isBuildingCampaign && <th className="text-left px-3 py-2">Unit</th>}
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Discount</th>
                  {isBuildingCampaign ? (
                    <>
                      <th className="text-left px-3 py-2">Uses</th>
                      <th className="text-left px-3 py-2">Total off</th>
                      <th className="text-left px-3 py-2">Last used</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Expiry</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayedCoupons.map((coupon) => (
                  <tr key={coupon.id} className="border-t">
                    {isBuildingCampaign && (
                      <td className="px-3 py-2 font-medium">{coupon.unitLabel ?? '—'}</td>
                    )}
                    <td className="px-3 py-2 font-mono">{coupon.code}</td>
                    <td className="px-3 py-2">{coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `${coupon.discountValue.toFixed(2)}`}</td>
                    {isBuildingCampaign ? (
                      <>
                        <td className="px-3 py-2">{coupon.usedCount ?? 0}</td>
                        <td className="px-3 py-2">${(coupon.totalDiscountGiven ?? 0).toFixed(2)}</td>
                        <td className="px-3 py-2">{coupon.usedAt ? new Date(coupon.usedAt).toLocaleDateString('en-NZ') : '—'}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2">{coupon.status}</td>
                        <td className="px-3 py-2">{coupon.endDate ? new Date(coupon.endDate).toLocaleDateString('en-NZ') : 'No expiry'}</td>
                      </>
                    )}
                  </tr>
                ))}
                {selectedCampaign && displayedCoupons.length === 0 && (
                  <tr><td colSpan={isBuildingCampaign ? 6 : 4} className="px-3 py-6 text-center text-gray-500">No coupons found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CouponCampaignsPage;

